import React, { useCallback, useEffect, useState } from 'react';
import {
  Apple, RefreshCw, Loader2, AlertCircle, Bell, ChevronDown, ChevronUp,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_COLORS = {
  SUBSCRIBED: 'text-[#22C55E] bg-[#22C55E]/10',
  DID_RENEW: 'text-[#3B82F6] bg-[#3B82F6]/10',
  EXPIRED: 'text-gray-400 bg-gray-500/10',
  GRACE_PERIOD_EXPIRED: 'text-yellow-400 bg-yellow-500/10',
  REFUND: 'text-red-400 bg-red-500/10',
  REVOKE: 'text-red-400 bg-red-500/10',
  DID_FAIL_TO_RENEW: 'text-orange-400 bg-orange-500/10',
};

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString();
  } catch {
    return String(d);
  }
};

/**
 * Admin card: lists the latest Apple App Store Server Notifications V2 events
 * and lets an admin re-fire the user-facing push for any of them.
 */
const AppleIapAuditCard = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const token = () => localStorage.getItem('session_token');

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/iap/audit?limit=100`, {
        headers: { Authorization: `Bearer ${token()}` },
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      const data = await res.json();
      setEvents(data.events || []);
    } catch (e) {
      setError(e.message || 'Failed to load Apple IAP audit log');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAudit();
  }, [fetchAudit]);

  const flashNotice = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(null), 2500);
  };

  const resendPush = async (uuid) => {
    if (!uuid) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/iap/audit/${encodeURIComponent(uuid)}/resend-push`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token()}` },
          credentials: 'include',
        },
      );
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      const data = await res.json();
      flashNotice(
        data.ok
          ? `Resent push to user ${data.resent_to_user_id?.slice(0, 12)}…`
          : data.reason || 'No push fired',
      );
    } catch (e) {
      setError(e.message || 'Resend failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="apple-iap-audit-card"
      className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-5 mt-6"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Apple className="h-5 w-5 text-white" />
          <h3 className="text-lg font-bold text-white">Apple IAP Audit</h3>
          <span className="text-xs text-gray-500">
            ({events.length} recent event{events.length === 1 ? '' : 's'})
          </span>
        </div>
        <button
          type="button"
          onClick={fetchAudit}
          disabled={loading || busy}
          data-testid="apple-iap-refresh"
          aria-label="Refresh"
          className="p-1.5 rounded-full bg-[#1e2a45] text-gray-400 hover:text-white hover:bg-[#2d3a56] transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <p className="text-xs text-gray-500 mb-4">
        Server-to-server renewal, cancellation, refund, and grace-period
        notifications received from the App Store. Use <b>Resend push</b> if a
        customer claims they didn&apos;t get a confirmation.
      </p>

      {error && (
        <div className="flex items-center gap-2 text-red-400 text-sm mb-3" data-testid="apple-iap-error">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}
      {notice && (
        <div className="text-[#22C55E] text-sm mb-3" data-testid="apple-iap-notice">
          {notice}
        </div>
      )}

      {loading && events.length === 0 ? (
        <div className="flex items-center justify-center py-6 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading audit log…
        </div>
      ) : events.length === 0 ? (
        <p className="text-xs text-gray-500 italic">
          No Apple IAP events recorded yet. Once the webhook is live in App Store
          Connect, every renewal/cancel/refund will appear here.
        </p>
      ) : (
        <div className="rounded-xl border border-[#1e2a45] overflow-hidden">
          <ul className="max-h-[480px] overflow-y-auto divide-y divide-[#1e2a45]">
            {events.map((ev) => {
              const uuid = ev.notification_uuid;
              const isOpen = expanded === uuid;
              const colorCls =
                TYPE_COLORS[ev.notification_type] || 'text-gray-300 bg-gray-700/30';
              return (
                <li
                  key={ev.audit_id}
                  data-testid={`apple-iap-row-${uuid || ev.audit_id}`}
                  className="px-3 py-3 hover:bg-[#0b1222]"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded ${colorCls}`}
                        >
                          {ev.notification_type}
                        </span>
                        {ev.subtype && (
                          <span className="text-[10px] text-gray-500 font-semibold">
                            {ev.subtype}
                          </span>
                        )}
                        {ev.environment && (
                          <span
                            className={`text-[10px] px-2 py-0.5 rounded ${
                              ev.environment === 'Production'
                                ? 'bg-[#1e2a45] text-gray-300'
                                : 'bg-yellow-500/10 text-yellow-400'
                            }`}
                          >
                            {ev.environment}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-sm text-white truncate">
                        {ev.user?.email || ev.user?.username || (
                          <span className="text-gray-500 italic">unmatched user</span>
                        )}
                        {ev.product_id && (
                          <span className="text-gray-500 ml-2">· {ev.product_id}</span>
                        )}
                      </div>
                      <div className="mt-0.5 text-[11px] text-gray-500">
                        {fmtDate(ev.received_at)}
                        {ev.original_transaction_id && (
                          <span className="ml-3">
                            txn: <code>{ev.original_transaction_id.slice(0, 16)}…</code>
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {uuid && (
                        <button
                          type="button"
                          onClick={() => resendPush(uuid)}
                          disabled={busy}
                          data-testid={`apple-iap-resend-${uuid}`}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#3B82F6]/20 text-[#3B82F6] hover:bg-[#3B82F6]/30 text-[11px] font-semibold transition-colors disabled:opacity-50"
                        >
                          <Bell className="h-3 w-3" />
                          Resend push
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setExpanded(isOpen ? null : uuid)}
                        aria-label="Toggle details"
                        className="p-1 rounded-full bg-[#1e2a45] text-gray-400 hover:text-white"
                      >
                        {isOpen ? (
                          <ChevronUp className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="mt-2 rounded-lg bg-[#050810] border border-[#1e2a45] p-3 text-[11px]">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-gray-300">
                        <div className="text-gray-500">Notification UUID</div>
                        <div className="break-all"><code>{uuid || '—'}</code></div>
                        <div className="text-gray-500">Transaction ID</div>
                        <div className="break-all"><code>{ev.transaction_id || '—'}</code></div>
                        <div className="text-gray-500">Original Txn ID</div>
                        <div className="break-all"><code>{ev.original_transaction_id || '—'}</code></div>
                        <div className="text-gray-500">Product ID</div>
                        <div><code>{ev.product_id || '—'}</code></div>
                        <div className="text-gray-500">Expires at</div>
                        <div>{fmtDate(ev.expires_at)}</div>
                        <div className="text-gray-500">Received at</div>
                        <div>{fmtDate(ev.received_at)}</div>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AppleIapAuditCard;
